import {
  Field,
  ImageField,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { DynamicHeading, isColorBright, resolveColorProperty } from '@gn-audio/jss-components';
import { ButtonVariantType } from '@gn-audio/jss-components/components/Button/Button';

import { Eyebrow } from 'common/components/Eyebrow';
import { mapSharedPropertiesToFields } from 'common/getters';
import NavButton from 'composables/shared/NavButton/NavButton';
import { ResponsiveImage } from 'composables/shared/ResponsiveImage/ResponsiveImage';
import { ComponentProps } from 'lib/component-props';

type ProductTopBannerFields = {
  title: Field<string>;
  titleSecondary: Field<string>;
  details: Field<string>;

  backgroundColor: Field<string>;
  desktopImage: ImageField;
  mobileImage: ImageField;

  buttonSize: Field<'xs' | 's' | 'm' | 'l'>;

  isMainHeading: Field<boolean>;

  showFirstButton: Field<boolean>;
  firstButtonTitle: Field<string>;
  firstButtonVariant: Field<ButtonVariantType>;
  firstButtonIcon: Field<keyof typeof icons>;
  firstButtonLink: LinkField;

  showSecondButton: Field<boolean>;
  secondButtonTitle: Field<string>;
  secondButtonVariant: Field<ButtonVariantType>;
  secondButtonIcon: Field<keyof typeof icons>;
  secondButtonLink: LinkField;

  imageContainerBackgroundColor: Field<string>;
  imageContainerIsFullWidth: Field<boolean>;
};

type ProductTopBannerWithSharedFields = {
  titleShared: Field<string>;
  titleSecondaryShared: Field<string>;
  detailsShared: Field<string>;
  firstButtonTitleShared: Field<string>;
  secondButtonTitleShared: Field<string>;
} & ProductTopBannerFields;

type ProductTopBannerProps = {
  fields: ProductTopBannerWithSharedFields;
} & ComponentProps;

const ProductTopBanner = ({ fields }: ProductTopBannerProps) => {
  const {
    backgroundColor,
    desktopImage,
    mobileImage,
    buttonSize,
    firstButtonVariant,
    firstButtonIcon,
    firstButtonLink,
    secondButtonVariant,
    secondButtonIcon,
    secondButtonLink,
    showFirstButton,
    showSecondButton,
    title,
    titleSecondary,
    details,
    firstButtonTitle,
    secondButtonTitle,
    isMainHeading,
    imageContainerBackgroundColor,
    imageContainerIsFullWidth,
  } = mapSharedPropertiesToFields<ProductTopBannerFields>(fields);

  const isBackgroundColorGradient = (backgroundColor: string | undefined) => {
    return backgroundColor?.startsWith('linear-gradient');
  };

  const imageBlockBackgroundColor = isBackgroundColorGradient(imageContainerBackgroundColor?.value)
    ? imageContainerBackgroundColor.value
    : resolveColorProperty(imageContainerBackgroundColor?.value) ?? 'transparent';

  const spotBackgroundColor =
    resolveColorProperty(backgroundColor?.value.toLowerCase()) ?? '#ffffff';

  const isBgBright = isColorBright(spotBackgroundColor);

  const imageBlockBackgroundStyle = {
    background: `${imageBlockBackgroundColor}`,
  };

  return (
    <div
      className={classNames(
        'container-fluid flex flex-col items-center gap-20',
        isBgBright ? 'light' : 'dark'
      )}
      style={{
        background: `${spotBackgroundColor}`,
      }}
    >
      <div className="container max-w-6xl !py-0 text-center">
        {titleSecondary?.value && <Eyebrow text={titleSecondary.value} align="center" />}

        <DynamicHeading isMainHeading={isMainHeading.value} className="heading-1 mt-5">
          <span dangerouslySetInnerHTML={{ __html: sanitize(title?.value) }}></span>
        </DynamicHeading>
        <p className="paragraph-large">{details.value}</p>
        {(showFirstButton?.value || showSecondButton?.value) && (
          <div className="mt-12 flex flex-wrap justify-center gap-6">
            {showFirstButton.value && (
              <NavButton
                variant={firstButtonVariant?.value}
                size={buttonSize?.value}
                link={firstButtonLink}
                icon={firstButtonIcon?.value}
                title={firstButtonTitle?.value}
              />
            )}
            {showSecondButton.value && (
              <NavButton
                variant={secondButtonVariant.value}
                size={buttonSize?.value}
                link={secondButtonLink}
                icon={secondButtonIcon?.value}
                title={secondButtonTitle?.value}
              />
            )}
          </div>
        )}
      </div>
      <div className="container-fluid !py-0" style={imageBlockBackgroundStyle}>
        <div
          className={classNames(
            '!py-0',
            imageContainerIsFullWidth?.value ? 'container-fluid' : 'container'
          )}
        >
          <ResponsiveImage desktopImage={desktopImage} mobileImage={mobileImage} />
        </div>
      </div>
    </div>
  );
};

export default withDatasourceCheck()(ProductTopBanner);
